import { Link, useSearchParams } from 'react-router-dom';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
} from '@/components/ui/pagination';
import { Helmet } from 'react-helmet-async';

interface BlogPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const buildPageUrl = (page: number, searchParams: URLSearchParams): string => {
  const params = new URLSearchParams(searchParams);
  if (page > 1) {
    params.set('page', page.toString());
  } else {
    params.delete('page');
  }
  const qs = params.toString();
  return `/blog${qs ? `?${qs}` : ''}`;
};

const BlogPagination = ({ currentPage, totalPages, onPageChange }: BlogPaginationProps) => {
  const [searchParams] = useSearchParams();

  if (totalPages <= 1) return null;

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots: (number | string)[] = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const visiblePages = getVisiblePages();

  const handleClick = (e: React.MouseEvent, page: number) => {
    e.preventDefault();
    onPageChange(page);
  };

  return (
    <>
      {/* SEO: rel prev/next for crawlers */}
      <Helmet>
        {currentPage > 1 && (
          <link rel="prev" href={`https://seniorthrive.com${buildPageUrl(currentPage - 1, searchParams)}`} />
        )}
        {currentPage < totalPages && (
          <link rel="next" href={`https://seniorthrive.com${buildPageUrl(currentPage + 1, searchParams)}`} />
        )}
      </Helmet>

      <Pagination className="mt-12">
        <PaginationContent>
          {currentPage > 1 && (
            <PaginationItem>
              <Link
                to={buildPageUrl(currentPage - 1, searchParams)}
                onClick={(e) => handleClick(e, currentPage - 1)}
                className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 cursor-pointer"
                rel="prev"
              >
                ← Previous
              </Link>
            </PaginationItem>
          )}
          
          {visiblePages.map((page, index) => (
            <PaginationItem key={`page-${page}-${index}`}>
              {page === '...' ? (
                <PaginationEllipsis />
              ) : (
                <Link
                  to={buildPageUrl(page as number, searchParams)}
                  onClick={(e) => handleClick(e, page as number)}
                  className={`inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 h-10 w-10 cursor-pointer ${
                    currentPage === page
                      ? 'border border-input bg-background hover:bg-accent hover:text-accent-foreground pointer-events-none'
                      : 'hover:bg-accent hover:text-accent-foreground'
                  }`}
                  aria-current={currentPage === page ? 'page' : undefined}
                >
                  {page}
                </Link>
              )}
            </PaginationItem>
          ))}
          
          {currentPage < totalPages && (
            <PaginationItem>
              <Link
                to={buildPageUrl(currentPage + 1, searchParams)}
                onClick={(e) => handleClick(e, currentPage + 1)}
                className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 cursor-pointer"
                rel="next"
              >
                Next →
              </Link>
            </PaginationItem>
          )}
        </PaginationContent>
      </Pagination>
    </>
  );
};

export default BlogPagination;
